import { inject, Injectable } from '@angular/core';
import { ApiConstantsService } from './api-constants.service';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { Pagination, UserPreferences } from '~core/models/core.models';
import {
  Organization,
  OrganizationCreationParams,
  OrganizationListParams,
  OrganizationUpdateParams,
  OrganizationUser,
  OrganizationUserScopeCreationParams
} from '~core/models/organization.models';

@Injectable()
export class OrganizationUsersService {
  private _http = inject(HttpClient);
  private _apiConstantsService = inject(ApiConstantsService);

  list(params?: OrganizationListParams): Observable<Pagination<Organization>> {
    return this._http.get<Pagination<Organization>>(
      this._apiConstantsService.organizationUrl,
      {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      }
    );
  }

  read(organizationId: string): Observable<Organization> {
    return this._http.get<Organization>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`
    );
  }

  create(payload: OrganizationCreationParams): Observable<Organization> {
    return this._http.post<Organization>(
      this._apiConstantsService.organizationUrl,
      payload
    );
  }

  update(
    organizationId: string,
    payload: OrganizationUpdateParams
  ): Observable<Organization> {
    return this._http.patch<Organization>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`,
      payload
    );
  }

  delete(organizationId: string): Observable<unknown> {
    return this._http.delete(
      `${this._apiConstantsService.organizationUrl}/${organizationId}`
    );
  }

  users(
    organizationId: string,
    params?: OrganizationListParams
  ): Observable<Pagination<OrganizationUser>> {
    return this._http.get<Pagination<OrganizationUser>>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users`,
      {
        params: {
          ...params,
          sort: params?.sort || 'createdAt',
          desc: params?.desc || 'createdAt'
        } as Record<string, any>
      }
    );
  }

  updateUserStatus(
    organizationId: string,
    userId: string,
    locked: boolean
  ): Observable<OrganizationUser> {
    return this._http.patch<OrganizationUser>(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users/${userId}`,
      {
        locked
      }
    );
  }

  userScope(organizationId: string, userId: string): Observable<unknown> {
    return this._http.get(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users/${userId}/scopes`
    );
  }

  updateUserScope(
    organizationId: string,
    userId: string,
    payload: OrganizationUserScopeCreationParams
  ): Observable<unknown> {
    return this._http.put(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users/${userId}/scopes`,
      payload
    );
  }

  /**
   *
   * @param organizationId  Set an user's scope role by establishment
   * @param userId
   * @param payload
   * @returns
   */
  setUserScope(
    organizationId: string,
    userId: string,
    payload: OrganizationUserScopeCreationParams
  ): Observable<unknown> {
    return this._http.put(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users/${userId}/scopes/set`,
      payload
    );
  }

  deleteUserScope(organizationId: string, userId: string): Observable<unknown> {
    return this._http.delete(
      `${this._apiConstantsService.organizationUrl}/${organizationId}/users/${userId}/scopes`
    );
  }

  preferences(organizationId: string): Observable<UserPreferences> {
    return this._http
      .get<{
        preference: UserPreferences;
      }>(
        `${this._apiConstantsService.organizationUrl}/${organizationId}/preference`
      )
      .pipe(map(res => res.preference));
  }

  setPreferences(
    organizationId: string,
    preferences: Partial<UserPreferences>
  ): Observable<UserPreferences> {
    return this._http
      .patch<{ preference: UserPreferences }>(
        `${this._apiConstantsService.organizationUrl}/${organizationId}/preference`,
        {
          preference: preferences
        }
      )
      .pipe(map(res => res.preference));
  }
}
